import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Download, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { fmtDateShort, fmtTime, STATUS_LABELS } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/admin/StatusBadge";
import { RegistrationSheet } from "@/components/admin/RegistrationSheet";

const COLS = [
  ["created_at", "Registered"], ["full_name", "Full name"], ["email", "Email"], ["whatsapp", "WhatsApp"], ["country", "Country"],
  ["budget", "Budget (USD)"], ["start_utc", "Appointment date"], [null, "Time (GST)"], ["status", "Status"],
];
const PAGE_SIZE = 10;

function Filters({ f, setF, onExport }) {
  const upd = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v, page: 1 }));
  return (
    <div className="flex flex-col lg:flex-row gap-3 mb-5">
      <div className="relative flex-1">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#697386]" />
        <input value={f.search} onChange={upd("search")} placeholder="Search name or email" className="field !pl-11" data-testid="registrations-search-input" />
      </div>
      <Select value={f.status} onValueChange={upd("status")}>
        <SelectTrigger className="field h-auto lg:w-44" data-testid="registrations-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`status-filter-${k}`}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <input type="date" value={f.date_from} onChange={upd("date_from")} className="field lg:w-44" aria-label="Appointment date from" data-testid="registrations-date-from" />
      <input type="date" value={f.date_to} onChange={upd("date_to")} className="field lg:w-44" aria-label="Appointment date to" data-testid="registrations-date-to" />
      <button onClick={onExport} className="btn-primary !py-2.5 whitespace-nowrap" data-testid="registrations-export-button"><Download size={15} /> Export CSV</button>
    </div>
  );
}

export default function Registrations() {
  const [f, setF] = useState({ search: "", status: "all", date_from: "", date_to: "", sort: "created_at", order: "desc", page: 1 });
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(f.search), 300);
    return () => clearTimeout(t);
  }, [f.search]);

  const params = useCallback(() => {
    const p = { search: q || undefined, status: f.status === "all" ? undefined : f.status, date_from: f.date_from || undefined, date_to: f.date_to || undefined };
    return p;
  }, [q, f.status, f.date_from, f.date_to]);

  const load = useCallback(() => {
    api.get("/admin/registrations", { params: { ...params(), sort: f.sort, order: f.order, page: f.page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [params, f.sort, f.order, f.page]);

  useEffect(() => {
    load();
  }, [load]);

  const sortBy = (k) => setF((s) => ({ ...s, sort: k, order: s.sort === k && s.order === "asc" ? "desc" : "asc", page: 1 }));

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/registrations/export.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fad-registrations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div data-testid="admin-registrations">
      <PageHeader title="Registrations" subtitle={data ? `${data.total} registration${data.total === 1 ? "" : "s"}` : "Loading…"} />
      <Filters f={f} setF={setF} onExport={exportCsv} />
      <div className="glass rounded-3xl overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="registrations-table">
          <thead>
            <tr className="text-left text-xs text-[#697386] border-b border-[rgba(20,35,55,0.10)]">
              {COLS.map(([k, l]) => (
                <th key={l} className="px-4 py-3.5 font-medium whitespace-nowrap">
                  {k ? (
                    <button onClick={() => sortBy(k)} className="flex items-center gap-1 hover:text-[#0B1F3A]" data-testid={`sort-${k}`}>
                      {l} {f.sort === k && (f.order === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                    </button>
                  ) : l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-[#697386]" data-testid="registrations-empty">No registrations match these filters.</td></tr>}
            {data?.items.map((r) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-b border-[rgba(20,35,55,0.06)] hover:bg-white/70 cursor-pointer transition-colors" data-testid={`registration-row-${r.id}`}>
                <td className="px-4 py-3.5 whitespace-nowrap text-[#697386]">{fmtDateShort(r.created_at)}</td>
                <td className="px-4 py-3.5 font-medium text-[#172033]">{r.full_name}</td>
                <td className="px-4 py-3.5">{r.email}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">{r.whatsapp}</td>
                <td className="px-4 py-3.5">{r.country}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">{r.budget || "—"}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">{fmtDateShort(r.start_utc)}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">{fmtTime(r.start_utc)}</td>
                <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-5 flex items-center justify-end gap-3 text-sm text-[#697386]">
        <span data-testid="registrations-page-info">Page {f.page} of {pages}</span>
        <button disabled={f.page <= 1} onClick={() => setF((s) => ({ ...s, page: s.page - 1 }))} className="h-9 w-9 grid place-items-center rounded-full border bg-white disabled:opacity-30" aria-label="Previous page" data-testid="registrations-prev-page"><ChevronLeft size={16} /></button>
        <button disabled={f.page >= pages} onClick={() => setF((s) => ({ ...s, page: s.page + 1 }))} className="h-9 w-9 grid place-items-center rounded-full border bg-white disabled:opacity-30" aria-label="Next page" data-testid="registrations-next-page"><ChevronRight size={16} /></button>
      </div>
      <RegistrationSheet id={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
